import React from "react";
import { Form, Modal } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { createCategory, updateCategory } from "../../../redux/slice/categorySlice";
import CommonButton from "../../../components/commonButton";
import { FormLabel } from "../../../components/form/utils";
import CustomSelect from "../../../components/form/CustomSelect";
import withModal from "../../../components/withModal";
import InputField from "../../../components/form/InputField";

const validationSchema = Yup.object().shape({
    name: Yup.string().required("Category name is required"),
    type: Yup.string().required("Category type is required"),
    is_active: Yup.boolean()
});

const CategoryModalInner = ({ onClose, item, refresh, isView }) => {
    const dispatch = useDispatch();
    const { config } = useSelector((state) => state.category);
    const isEdit = Boolean(item);

    const initialValues = {
        name: item?.name || "",
        type: item?.type || "interest",
        is_active: item?.is_active ?? true,
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            if (isEdit) {
                await dispatch(updateCategory({ id: item.id, data: values })).unwrap();
            } else {
                await dispatch(createCategory(values)).unwrap();
            }
            refresh();
            onClose();
        } catch (error) {
            console.error("Failed to save category:", error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
        >
            {({ values, errors, touched, handleChange, handleSubmit, isSubmitting, setFieldValue }) => (
                <Form onSubmit={handleSubmit}>
                    <Modal.Body className="pt-4">
                        <InputField className="mb-3"
                            label={{ name: "Category Name" }}
                            input={{
                                id: "name",
                                name: "name",
                                placeholder: "Enter category name",
                                value: values.name,
                                onChange: handleChange,
                                disabled: isView
                            }}
                            validation={{
                                isInvalid: touched.name && errors.name,
                                message: errors.name,
                            }} />

                        <CustomSelect
                            className="mb-3"
                            label={{ name: "Category Type" }}
                            isCreatable={true}
                            options={config?.types || []}
                            value={config?.types?.find(t => t.value === values.type) || { value: values.type, label: values.type.charAt(0).toUpperCase() + values.type.slice(1) }}
                            onChange={(opt) => setFieldValue("type", opt ? opt.value : "")}
                            isClearable={false}
                            isDisabled={isView}
                            validation={{
                                isInvalid: touched.type && errors.type,
                                message: errors.type,
                            }}
                        />

                        <div className="mb-3">
                            <FormLabel labelName="Status" />
                            <div className="mt-1">
                                <Form.Check
                                    type="switch"
                                    name="is_active"
                                    label={values.is_active ? "Active" : "Inactive"}
                                    checked={values.is_active}
                                    onChange={(e) => setFieldValue("is_active", e.target.checked)}
                                    className="cursor-pointer custom-plan-switch"
                                    disabled={isView}
                                />
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0">
                        <CommonButton
                            title="Cancel"
                            variant="outline"
                            onClick={onClose}
                            type="button"
                        />
                        {!isView && (
                            <CommonButton
                                title={isSubmitting ? "Saving..." : isEdit ? "Update" : "Create"}
                                type="submit"
                                variant="primary"
                                disabled={isSubmitting}
                            />
                        )}
                    </Modal.Footer>
                </Form>
            )}
        </Formik>
    );
};

const CategoryModal = withModal(CategoryModalInner);

export default CategoryModal;
